import React from 'react';
import classNames from 'classnames';
// import PropTypes from 'prop-types';
import { NavLink } from 'react-router-dom';
// @material-ui/core components
import Drawer from '@mui/material/Drawer';
import Icon from '@mui/material/Icon';
import Divider from '@mui/material/Divider';

import { Kontingent } from './Kontingent';

interface SidebarProps {
  color: string;
  logo: string;
  image: string;
  logoText: string;
  routes: any[];
  rtlActive?: boolean;
  open: boolean;
  handleDrawerToggle: () => void;
  username: string;
  location: any;
  navigate: any;
}

const Sidebar = React.forwardRef<HTMLDivElement, SidebarProps>((props: SidebarProps) => {
  // verifies if routeName is the one active (in browser input)
  function activeRoute(routeName: any) {
    return props.location.pathname.indexOf(routeName) > -1 ? true : false;
  }
  const { color, logo, image, logoText, routes } = props;
  
  // Get the first letter of username for avatar
  const userInitial = props.username ? props.username.charAt(0).toUpperCase() : 'U';
  
  // Generate avatar background color classes based on color prop
  const getAvatarColorClass = () => {
    switch(color) {
      case 'blue': return 'bg-blue-500';
      case 'green': return 'bg-green-500';
      case 'red': return 'bg-red-500';
      case 'orange': return 'bg-orange-500';
      case 'purple': return 'bg-purple-500';
      default: return 'bg-blue-500';
    }
  };
  
  // Generate active menu item background color
  const getActiveColorClass = () => {
    switch(color) {
      case 'blue': return 'bg-blue-500/80';
      case 'green': return 'bg-green-500/80';
      case 'red': return 'bg-red-500/80';
      case 'orange': return 'bg-orange-500/80';
      case 'purple': return 'bg-purple-500/80';
      default: return 'bg-blue-500/80';
    }
  };
  
  var links = (
    <ul className="mt-6 pl-0 pt-0 pb-0 mb-0 list-none">
      {routes.map((prop: any, key: any) => {
        let activePro = '';
        
        // Handle special kontingent path
        if(prop.path === '/kontingent') {
          return (
            <NavLink
              to={prop.layout + prop.path}
              className={({ isActive }) => `relative block ${isActive ? 'text-white' : 'text-white'}`}
              key={key}
            >
              <li className="w-auto transition-all duration-300 m-3 rounded-md relative block p-3 bg-transparent hover:bg-white/5 hover:translate-x-1">
                <Kontingent username={props.username}/>
              </li>
            </NavLink>
          )
        }
        
        // Check if route is active
        const isActive = activeRoute(prop.layout + prop.path);
        
        return (
          <NavLink
            to={prop.layout + prop.path}
            className={`relative block ${activePro}`}
            key={key}
          >
            <li className={classNames(
              "w-auto transition-all duration-300 m-3 rounded-md relative block p-3 bg-transparent hover:bg-white/5 hover:translate-x-1",
              { [getActiveColorClass()]: isActive }
            )}>
              {typeof prop.icon === 'string' ? (
                <Icon
                  className={classNames(
                    "w-6 h-8 text-2xl leading-8 float-left mr-4 text-center align-middle text-white/80",
                    { "mr-1 ml-4 float-right": props.rtlActive }
                  )}
                >
                  {prop.icon}
                </Icon>
              ) : (
                <prop.icon
                  className={classNames(
                    "w-6 h-8 text-2xl leading-8 float-left mr-4 text-center align-middle text-white/80",
                    { "mr-1 ml-4 float-right": props.rtlActive }
                  )}
                />
              )}
              <div
                className={classNames(
                  "m-0 leading-8 text-sm text-white font-normal tracking-wide",
                  { "text-right": props.rtlActive }
                )}
              >
                {props.rtlActive ? prop.rtlName : prop.name}
              </div>
              {isActive && (
                <div className="w-1 h-full absolute right-0 top-0 bg-white rounded-l-md" />
              )}
            </li>
          </NavLink>
        );
      })}
    </ul>
  );
  
  // User profile section
  const userProfile = (
    <div className="px-6 py-4 flex items-center mb-1">
      <div className={`${getAvatarColorClass()} w-[42px] h-[42px] rounded-full flex items-center justify-center text-white shadow-md`}>
        {userInitial}
      </div>
      <div className="ml-2">
        <div className="text-white font-medium">
          {props.username}
        </div>
        <div className="text-xs text-white/70">
          User
        </div>
      </div>
    </div>
  );
  
  var brand = (
    <div className="relative py-6 px-4 z-10 flex items-center justify-start after:content-[''] after:absolute after:bottom-0 after:h-px after:right-4 after:w-[calc(100%-30px)] after:bg-gray-600/30">
      <a
        href="https://www.creative-tim.com"
        className={classNames(
          "uppercase py-1.5 flex items-center text-lg text-left font-medium leading-8 no-underline tracking-wider bg-transparent text-white",
          { "text-right": props.rtlActive }
        )}
      >
        <div className="w-[34px] inline-block max-h-[34px] ml-0 mr-3 transition-all duration-300">
          <img src={logo} alt="logo" className="w-[35px] top-[22px] absolute align-middle border-0 filter drop-shadow-md" />
        </div>
        {logoText}
      </a>
    </div>
  );
  
  const drawerPaperClass = classNames(
    "border-none fixed top-0 bottom-0 left-0 z-10 shadow-md w-[260px] bg-gradient-to-b from-[#2c3e50] to-[#1a2632] rounded-r-lg",
    {
      "left-auto right-0": props.rtlActive && window.innerWidth < 960,
      "right-auto left-0": !props.rtlActive || window.innerWidth >= 960
    }
  );
  
  return (
    <div>
      {/* Mobile drawer */}
      <div className="md:hidden block">
        <Drawer
          variant="temporary"
          anchor={props.rtlActive ? 'left' : 'right'}
          open={props.open}
          classes={{
            paper: drawerPaperClass
          }}
          onClose={props.handleDrawerToggle}
          ModalProps={{
            keepMounted: true // Better open performance on mobile.
          }}
        >
          {brand}
          <Divider className="bg-white/10 my-1"/>
          {userProfile}
          <div className="relative h-[calc(100vh-75px)] overflow-auto w-[260px] z-10">{links}</div>
          {image !== undefined ? (
            <div
              className="absolute z-[1] h-full w-full block top-0 left-0 bg-cover bg-center after:absolute after:z-[3] after:w-full after:h-full after:content-[''] after:block after:bg-black after:opacity-80"
              style={{ backgroundImage: 'url(' + image + ')' }}
            />
          ) : null}
        </Drawer>
      </div>
      
      {/* Desktop drawer */}
      <div className="xs:hidden md:block">
        <Drawer
          anchor={props.rtlActive ? 'right' : 'left'}
          variant="permanent"
          open={true}
          classes={{
            paper: drawerPaperClass
          }}
        >
          {brand}
          <Divider className="bg-white/10 my-1"/>
          {userProfile}
          <div className="relative h-[calc(100vh-75px)] overflow-auto w-[260px] z-10 [scrollbar-width:thin] [&::-webkit-scrollbar]:w-[6px] [&::-webkit-scrollbar]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-gray-400/30 [&::-webkit-scrollbar-thumb]:rounded-[10px]">{links}</div>
          {image !== undefined ? (
            <div
              className="absolute z-[1] h-full w-full block top-0 left-0 bg-cover bg-center after:absolute after:z-[3] after:w-full after:h-full after:content-[''] after:block after:bg-black after:opacity-80"
              style={{ backgroundImage: 'url(' + image + ')' }}
            />
          ) : null}
        </Drawer>
      </div>
    </div>
  );
});

// Sidebar.propTypes = {
//   classes: PropTypes.object.isRequired
// };

export default Sidebar;